// ==========================================
// src/pages/NovaCarga.jsx
// CADASTRO DE CARGA | Dados Empresa Protegidos | Preview Seguro
// ✅ SUPABASE: Salvar carga via RPC (bypass RLS 403)
// ✅ LOCALSTORAGE: Salvar carga para admin visualizar
// ✅ EMPRESA APROVADA: Publica sem autorização do admin
// ✅ CORRIGIDO: peso_valor, peso_unidade
// ✅ NOVO: Botão Gerar Link de Divulgação com WhatsApp
// ✅ FIX: Link usa página estática carga-preview.html no Hostinger
// ==========================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, MapPin, Truck, Calendar, DollarSign, ArrowLeft, Loader2,
  CheckCircle, Download, Upload, FileText, Weight, Box, Info, List,
  Building2, Phone, User, Shield,
  // ✅ NOVOS ÍCONES para divulgação:
  Share2, Copy, Check
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from '@/lib/AuthContext';
import { supabase } from '@/lib/supabase';

const estadosBrasil = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const tiposVeiculo = [
  { value: 'truck', label: 'Truck' },
  { value: 'carreta', label: 'Carreta' },
  { value: 'bitrem', label: 'Bitrem' },
  { value: 'rodotrem', label: 'Rodotrem' },
  { value: 'van', label: 'Van' },
  { value: 'toco', label: 'Toco' },
  { value: 'tres_quartos', label: '3/4' },
  { value: 'vuc', label: 'VUC' },
  { value: 'prancha', label: 'Prancha' },
  { value: 'furgão', label: 'Furgão' }
];

const tiposCarga = [
  { value: 'geral', label: 'Geral' },
  { value: 'granel_solido', label: 'Granel Sólido' },
  { value: 'granel_liquido', label: 'Granel Líquido' },
  { value: 'refrigerada', label: 'Refrigerada' },
  { value: 'perigosa', label: 'Perigosa' },
  { value: 'viva', label: 'Viva' },
  { value: 'indivisivel', label: 'Indivisível' },
  { value: 'container', label: 'Container' },
  { value: 'sacos', label: 'Sacos' },
  { value: 'big_bag', label: 'Big Bag' },
  { value: 'paletizado', label: 'Paletizado' }
];

const tiposCarroceria = [
  { value: 'aberta', label: 'Aberta' },
  { value: 'fechada', label: 'Fechada' },
  { value: 'bau', label: 'Baú' },
  { value: 'sider', label: 'Sider' },
  { value: 'graneleira', label: 'Graneleira' },
  { value: 'tanque', label: 'Tanque' },
  { value: 'cegonha', label: 'Cegonha' },
  { value: 'prancha', label: 'Prancha' },
  { value: 'boiadeiro', label: 'Boiadeiro' },
  { value: 'cacamba', label: 'Caçamba' }
];

const tiposValor = [
  { value: 'fixo', label: 'Informar Valor', descricao: 'Valor fixo do frete' },
  { value: 'a_combinar', label: 'A Combinar', descricao: 'Valor a negociar' },
  { value: 'tonelada', label: 'Tonelada', descricao: 'Valor por tonelada' },
  { value: 'metro_cubico', label: 'Metros Cúbicos', descricao: 'Valor por m³' }
];

const unidadesPeso = [
  { value: 'kg', label: 'Quilogramas (kg)', icon: Weight },
  { value: 'ton', label: 'Toneladas (ton)', icon: Weight },
  { value: 'm3', label: 'Metros Cúbicos (m³)', icon: Box }
];

const frasesDescricao = [
  { value: 'carregamento_imediato', label: 'Carregamento Imediato', texto: 'Carregamento Imediato.' },
  { value: 'sem_agendamento', label: 'Sem Agendamento', texto: 'Sem agendamento.' },
  { value: 'carga_frigorificada', label: 'Carga Frigorificada', texto: 'Carga frigorificada - manter temperatura controlada.' },
  { value: 'carga_perigosa', label: 'Carga Perigosa', texto: 'Carga perigosa - necessário habilitação MOPP.' },
  { value: 'carga_viva', label: 'Carga Viva', texto: 'Carga viva - manter ventilação.' },
  { value: 'lona_obrigatoria', label: 'Lona Obrigatória', texto: 'Transporte com lona obrigatória.' },
  { value: 'rastreador_obrigatorio', label: 'Rastreador Obrigatório', texto: 'Veículo com rastreador obrigatório.' },
  { value: 'seguro_obrigatorio', label: 'Seguro Obrigatório', texto: 'Seguro de carga obrigatório.' }
];

const parseNumero = (valor) => {
  if (!valor || valor === '') return 0;
  const str = String(valor).trim().replace(/\./g, '').replace(',', '.');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const formatarDataInput = (data) => {
  return data.toISOString().split('T')[0];
};

const formatarValor = (valor) => {
  const num = Number(valor);
  if (isNaN(num) || num === 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { 
    style: 'currency', 
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

export default function NovaCarga() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [empresaAprovada, setEmpresaAprovada] = useState(false);
  const [verificandoEmpresa, setVerificandoEmpresa] = useState(true);

  // ✅ NOVO: States para link de divulgação
  const [linkDivulgacao, setLinkDivulgacao] = useState(null);
  const [linkCopiado, setLinkCopiado] = useState(false);

  const hoje = new Date();
  const daqui5Dias = new Date(hoje);
  daqui5Dias.setDate(hoje.getDate() + 5);

  const [formData, setFormData] = useState({
    titulo: '',
    tipo_carga: '',
    peso_valor: '',
    peso_unidade: 'ton',
    metros_cubicos: '',
    origem_cidade: '',
    origem_estado: '',
    destino_cidade: '',
    destino_estado: '',
    data_coleta: formatarDataInput(hoje),
    data_entrega: formatarDataInput(daqui5Dias),
    tipo_valor: 'fixo',
    valor_frete: '',
    frases_selecionadas: ['carregamento_imediato', 'sem_agendamento'],
    tipo_veiculo: '',
    tipo_carroceria: '',
    empresa_id: '',
    empresa_nome: '',
    empresa_cnpj: '',
    empresa_telefone: '',
    empresa_email: '',
    empresa_cidade: '',
    empresa_estado: '',
    empresa_responsavel: '',
    created_by: ''
  });

  // ============================================================
  // ✅ VERIFICAR SE EMPRESA ESTÁ APROVADA NO SUPABASE
  // ============================================================
  useEffect(() => {
    const verificarEmpresa = async () => {
      if (!user?.id) {
        setVerificandoEmpresa(false);
        return;
      }

      try {
        console.log('🔍 Verificando empresa aprovada para user:', user.id);

        const { data, error } = await supabase
          .from('empresas')
          .select('status, nome_fantasia, razao_social, cnpj, telefone, email, cidade, estado')
          .eq('usuario_id', user.id)
          .single();

        if (error) {
          console.error('❌ Erro ao buscar empresa:', error);
          setEmpresaAprovada(false);
          setVerificandoEmpresa(false);
          return;
        }

        console.log('📋 Dados empresa:', data);

        if (data?.status === 'aprovado') {
          setEmpresaAprovada(true);
          
          setFormData(prev => ({
            ...prev,
            empresa_id: user.id,
            empresa_nome: data.nome_fantasia || data.razao_social || 'Empresa',
            empresa_cnpj: data.cnpj || '',
            empresa_telefone: data.telefone || '',
            empresa_email: data.email || '',
            empresa_cidade: data.cidade || '',
            empresa_estado: data.estado || '',
            empresa_responsavel: data.razao_social || '',
            created_by: user.email || ''
          }));
        } else {
          setEmpresaAprovada(false);
          toast.error('⚠️ Sua empresa ainda não foi aprovada pelo administrador. Aguarde aprovação para publicar cargas.');
        }
      } catch (err) {
        console.error('❌ Erro verificação:', err);
        setEmpresaAprovada(false);
      } finally {
        setVerificandoEmpresa(false);
      }
    };

    verificarEmpresa();
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFrase = (fraseValue) => {
    setFormData(prev => {
      const atual = prev.frases_selecionadas || [];
      if (atual.includes(fraseValue)) {
        return { ...prev, frases_selecionadas: atual.filter(f => f !== fraseValue) };
      } else {
        return { ...prev, frases_selecionadas: [...atual, fraseValue] };
      }
    });
  };

  const montarDescricao = () => {
    const frases = formData.frases_selecionadas || [];
    const textos = frases.map(f => {
      const frase = frasesDescricao.find(fd => fd.value === f);
      return frase ? frase.texto : '';
    }).filter(t => t);
    return textos.join(' ');
  };

  const calcularValorTotal = () => {
    const valor = parseNumero(formData.valor_frete);
    const peso = parseNumero(formData.peso_valor);
    const m3 = parseNumero(formData.metros_cubicos);

    let total = 0;

    if (formData.tipo_valor === 'tonelada' && peso > 0 && valor > 0) {
      total = valor * peso;
    } else if (formData.tipo_valor === 'metro_cubico' && m3 > 0 && valor > 0) {
      total = valor * m3;
    } else if (formData.tipo_valor === 'fixo' && valor > 0) {
      total = valor;
    }

    return total;
  };

  // ============================================================
  // ✅ FUNÇÃO: Gerar slug (mantida para compatibilidade)
  // ============================================================
  const gerarSlug = (cargaData) => {
    if (!cargaData) return '';
    
    const origem = `${cargaData.origem_cidade}-${cargaData.origem_estado}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const destino = `${cargaData.destino_cidade}-${cargaData.destino_estado}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const tipo = (cargaData.tipo_carga || 'carga')
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/-+/g, '-');
    
    const peso = cargaData.peso_valor ? `${cargaData.peso_valor}-${cargaData.peso_unidade || 'ton'}` : '';
    
    const idCurto = cargaData.id?.split('-')[0] || '';
    
    const partes = ['frete', origem, 'para', destino];
    if (tipo && tipo !== 'carga') partes.push(tipo);
    if (peso) partes.push(peso);
    partes.push(idCurto);
    
    return partes.join('-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  // ============================================================
  // ✅ FUNÇÃO: Gerar link completo de divulgação (página estática no Hostinger)
  // ✅ FIX: Usa carga-preview.html para WhatsApp mostrar só texto da carga
  // ============================================================
  const gerarLinkDivulgacao = (cargaLocal) => {
    const params = new URLSearchParams({
      id: cargaLocal.id,
      origem: cargaLocal.origem_cidade || '',
      origem_uf: cargaLocal.origem_estado || '',
      destino: cargaLocal.destino_cidade || '',
      destino_uf: cargaLocal.destino_estado || '',
      titulo: cargaLocal.titulo || '',
      valor: cargaLocal.valor_frete ? 'R$ ' + cargaLocal.valor_frete : 'A combinar',
      peso: (cargaLocal.peso_valor || '') + ' ' + (cargaLocal.peso_unidade || ''),
      veiculo: cargaLocal.tipo_veiculo || '',
      tipo: cargaLocal.tipo_carga || ''
    });
    
    // ✅ USA PÁGINA ESTÁTICA NO HOSTINGER (funciona 100%, não depende do Supabase)
    // WhatsApp lê os meta tags e mostra só texto da carga, sem imagem grande do Borinho
    return `https://borafrete.com.br/carga-preview.html?${params.toString()}`;
  };

  // ============================================================
  // ✅ AÇÕES DO LINK DE DIVULGAÇÃO
  // ============================================================
  
  const copiarLink = () => {
    if (!linkDivulgacao) return;
    navigator.clipboard.writeText(linkDivulgacao).then(() => {
      setLinkCopiado(true);
      toast.success('Link copiado! Cole no WhatsApp.');
      setTimeout(() => setLinkCopiado(false), 3000);
    });
  };

  const compartilharWhatsApp = () => {
    if (!linkDivulgacao) return;
    const valorTotalCalculado = calcularValorTotal();
    const texto = `🚛 *FRETE DISPONÍVEL no BoraFrete!*%0A%0A` +
      `📦 *${formData.titulo}*%0A` +
      `📍 *Origem:* ${formData.origem_cidade}/${formData.origem_estado}%0A` +
      `🏁 *Destino:* ${formData.destino_cidade}/${formData.destino_estado}%0A` +
      `⚖️ *Peso:* ${formData.peso_valor} ${formData.peso_unidade || 'ton'}%0A` +
      `💰 *Valor:* ${formatarValor(valorTotalCalculado)}%0A%0A` +
      `👉 Clique e se candidate:%0A${linkDivulgacao}`;
    
    window.open(`https://wa.me/?text=${texto}`, '_blank');
  };

  const fecharEIrParaCargas = () => {
    setLinkDivulgacao(null);
    navigate('/minhas-cargas');
  };

  // ============================================================
  // ✅ SUPABASE: Salvar carga via RPC + LOCALSTORAGE
  // ============================================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('🚀 Iniciando publicação via RPC...');

    if (!empresaAprovada) {
      toast.error('⚠️ Sua empresa precisa estar aprovada para publicar cargas.');
      return;
    }

    if (!formData.titulo || !formData.origem_cidade || !formData.origem_estado || 
        !formData.destino_cidade || !formData.destino_estado || !formData.tipo_veiculo ||
        !formData.peso_valor) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    if (formData.tipo_valor !== 'a_combinar' && !formData.valor_frete) {
      toast.error('Informe o valor do frete');
      return;
    }

    if (formData.frases_selecionadas.length === 0) {
      toast.error('Selecione pelo menos uma frase para a descrição');
      return;
    }

    if (!formData.empresa_id) {
      console.error('❌ Sem empresa_id:', formData);
      toast.error('Erro: ID da empresa não encontrado. Faça login novamente.');
      return;
    }

    setIsSaving(true);

    try {
      const descricaoFinal = montarDescricao();
      const valorTotalCalculado = calcularValorTotal();

      console.log('💰 Valor total calculado:', valorTotalCalculado);
      console.log('📤 Enviando empresa_id:', formData.empresa_id);
      console.log('📤 Peso valor:', parseNumero(formData.peso_valor));
      console.log('📤 Peso unidade:', formData.peso_unidade);

      // ✅ USA RPC em vez de .insert() - bypass RLS 403
      const { data: resultado, error } = await supabase.rpc('inserir_carga', {
        p_titulo: formData.titulo,
        p_tipo_carga: formData.tipo_carga,
        p_peso_valor: parseNumero(formData.peso_valor),
        p_peso_unidade: formData.peso_unidade,
        p_metros_cubicos: parseNumero(formData.metros_cubicos),
        p_origem_cidade: formData.origem_cidade,
        p_origem_estado: formData.origem_estado,
        p_destino_cidade: formData.destino_cidade,
        p_destino_estado: formData.destino_estado,
        p_data_coleta: formData.data_coleta,
        p_data_entrega: formData.data_entrega,
        p_tipo_valor: formData.tipo_valor,
        p_valor_frete: formData.tipo_valor === 'a_combinar' ? null : parseNumero(formData.valor_frete),
        p_valor_total: valorTotalCalculado,
        p_tipo_veiculo: formData.tipo_veiculo,
        p_tipo_carroceria: formData.tipo_carroceria,
        p_descricao: descricaoFinal,
        p_empresa_id: formData.empresa_id,
        p_empresa_nome: formData.empresa_nome,
        p_empresa_cnpj: formData.empresa_cnpj,
        p_empresa_telefone: formData.empresa_telefone,
        p_empresa_email: formData.empresa_email,
        p_empresa_cidade: formData.empresa_cidade,
        p_empresa_estado: formData.empresa_estado,
        p_empresa_responsavel: formData.empresa_responsavel
      });

      if (error) {
        console.error('❌ Erro RPC:', error);
        throw error;
      }

      if (!resultado || !resultado.success) {
        console.error('❌ RPC retornou erro:', resultado?.error || 'Resposta inválida');
        throw new Error(resultado?.error || 'Erro desconhecido ao salvar carga');
      }

      console.log('✅ Carga salva via RPC:', resultado.carga_id);

      // ✅ NOVO: Salvar no localStorage também para o admin visualizar
      const cargaLocal = {
        id: resultado.carga_id,  // ID real do Supabase
        titulo: formData.titulo,
        tipo_carga: formData.tipo_carga,
        peso_valor: parseNumero(formData.peso_valor),
        peso_unidade: formData.peso_unidade,
        metros_cubicos: parseNumero(formData.metros_cubicos) || null,
        origem_cidade: formData.origem_cidade,
        origem_estado: formData.origem_estado,
        destino_cidade: formData.destino_cidade,
        destino_estado: formData.destino_estado,
        data_coleta: formData.data_coleta,
        data_entrega: formData.data_entrega,
        tipo_valor: formData.tipo_valor,
        valor_frete: formData.tipo_valor === 'a_combinar' ? null : parseNumero(formData.valor_frete),
        valor_total: valorTotalCalculado,
        tipo_veiculo: formData.tipo_veiculo,
        tipo_carroceria: formData.tipo_carroceria,
        descricao: descricaoFinal,
        status: 'disponivel',
        empresa_id: formData.empresa_id,
        empresa_nome: formData.empresa_nome,
        empresa_cnpj: formData.empresa_cnpj,
        empresa_telefone: formData.empresa_telefone,
        empresa_email: formData.empresa_email,
        empresa_cidade: formData.empresa_cidade,
        empresa_estado: formData.empresa_estado,
        empresa_responsavel: formData.empresa_responsavel,
        created_by: user?.email || '',
        created_at: new Date().toISOString()
      };

      const cargasExistentes = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
      cargasExistentes.unshift(cargaLocal);  // Adiciona no início
      localStorage.setItem('borafrete_cargas', JSON.stringify(cargasExistentes));

      console.log('✅ Carga salva no localStorage:', cargaLocal.id);

      // ✅ NOVO: Gerar link de divulgação
      const link = gerarLinkDivulgacao(cargaLocal);
      setLinkDivulgacao(link);
      console.log('🔗 Link de divulgação gerado:', link);

      toast.success(`✅ Carga "${formData.titulo}" publicada! Valor: ${formatarValor(valorTotalCalculado)}`);

      // Navegação automática DESATIVADA — aguarda usuário copiar link ou fechar modal
      // setTimeout(() => { navigate('/minhas-cargas'); }, 800);

    } catch (error) {
      console.error('❌ Erro:', error);
      toast.error('Erro ao publicar carga: ' + (error.message || error));
    } finally {
      setIsSaving(false);
    }
  };

  const downloadCSVTemplate = () => {
    const headers = [
      'titulo', 'tipo_carga', 'peso_valor', 'peso_unidade', 'metros_cubicos',
      'origem_cidade', 'origem_estado', 'destino_cidade', 'destino_estado',
      'data_coleta', 'data_entrega', 'tipo_valor', 'valor_frete',
      'tipo_veiculo', 'tipo_carroceria', 'descricao',
      'empresa_id', 'empresa_nome', 'empresa_cnpj', 'empresa_telefone', 'empresa_responsavel'
    ];
    const exemplo = [
      'Carga de Grãos', 'granel_solido', '45', 'ton', '60',
      'Cuiabá', 'MT', 'Santos', 'SP', '2026-03-01', '2026-03-05',
      'tonelada', '150.00', 'carreta', 'graneleira',
      'Carregamento Imediato. Sem agendamento.',
      '123', 'Empresa XYZ', '00.000.000/0000-00', '(65) 98118-1997', 'João Silva'
    ];
    const csv = [headers.join(','), exemplo.join(',')].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'modelo_fretes_borafrete.csv';
    link.click();
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (file) setCsvFile(file);
  };

  const importCSV = async () => {
    if (!csvFile) {
      toast.error('Selecione um arquivo CSV');
      return;
    }
    
    if (!empresaAprovada) {
      toast.error('⚠️ Sua empresa precisa estar aprovada para importar cargas.');
      return;
    }
    
    setIsImporting(true);
    try {
      const text = await csvFile.text();
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());

      const cargas = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = lines[i].split(',').map(v => v.trim());
        const carga = {};

        headers.forEach((header, index) => {
          let value = values[index] || '';
          if (['peso_valor', 'metros_cubicos', 'valor_frete'].includes(header)) {
            value = value ? parseNumero(value) : 0;
          }
          carga[header] = value;
        });

        const tipoValor = carga.tipo_valor || 'fixo';
        const valorFrete = parseNumero(carga.valor_frete);
        const peso = parseNumero(carga.peso_valor);
        const m3 = parseNumero(carga.metros_cubicos);

        let valorTotal = 0;
        if (tipoValor === 'tonelada' && peso > 0) {
          valorTotal = valorFrete * peso;
        } else if (tipoValor === 'metro_cubico' && m3 > 0) {
          valorTotal = valorFrete * m3;
        } else if (tipoValor === 'fixo') {
          valorTotal = valorFrete;
        }

        const empresaId = user?.id || '';

        cargas.push({
          titulo: carga.titulo,
          tipo_carga: carga.tipo_carga,
          peso_valor: parseNumero(carga.peso_valor),
          peso_unidade: carga.peso_unidade || 'ton',
          metros_cubicos: parseNumero(carga.metros_cubicos),
          origem_cidade: carga.origem_cidade,
          origem_estado: carga.origem_estado,
          destino_cidade: carga.destino_cidade,
          destino_estado: carga.destino_estado,
          data_coleta: carga.data_coleta,
          data_entrega: carga.data_entrega,
          tipo_valor: carga.tipo_valor,
          valor_frete: parseNumero(carga.valor_frete),
          valor_total: valorTotal,
          tipo_veiculo: carga.tipo_veiculo,
          tipo_carroceria: carga.tipo_carroceria,
          descricao: carga.descricao,
          status: 'disponivel',
          empresa_id: empresaId,
          empresa_nome: carga.empresa_nome || formData.empresa_nome || 'Empresa',
          empresa_razao_social: carga.empresa_nome || formData.empresa_nome || 'Empresa',
          empresa_cnpj: carga.empresa_cnpj || formData.empresa_cnpj || 'Não informado',
          empresa_telefone: carga.empresa_telefone || formData.empresa_telefone || 'Não informado',
          empresa_celular: carga.empresa_telefone || formData.empresa_telefone || 'Não informado',
          empresa_email: carga.empresa_email || formData.empresa_email || user?.email || 'Não informado',
          empresa_cidade: carga.empresa_cidade || formData.empresa_cidade || 'Não informado',
          empresa_estado: carga.empresa_estado || formData.empresa_estado || 'Não informado',
          empresa_responsavel: carga.empresa_responsavel || formData.empresa_responsavel || 'Não informado',
          empresa_endereco: user?.endereco || 'Não informado',
          created_by: user?.email || ''
        });
      }

      if (cargas.length === 0) {
        toast.error('Nenhuma carga encontrada no arquivo');
        setIsImporting(false);
        return;
      }

      const { data, error } = await supabase
        .from('cargas')
        .insert(cargas);

      if (error) throw error;

      // ✅ NOVO: Salvar cargas importadas no localStorage também
      const cargasExistentes = JSON.parse(localStorage.getItem('borafrete_cargas') || '[]');
      const cargasComId = (data || []).map((c, index) => ({
        ...cargas[index],
        id: c.id,
        created_at: new Date().toISOString()
      }));
      localStorage.setItem('borafrete_cargas', JSON.stringify([...cargasComId, ...cargasExistentes]));

      toast.success(`${cargas.length} carga(s) importada(s)!`);
      setCsvFile(null);
      setTimeout(() => navigate('/minhas-cargas'), 1000);
    } catch (error) {
      console.error('Erro importação:', error);
      toast.error('Erro ao importar CSV: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  const renderValorPreview = () => {
    const valor = parseNumero(formData.valor_frete);
    const peso = parseNumero(formData.peso_valor);
    const m3 = parseNumero(formData.metros_cubicos);
    
    const unidadeLabel = formData.peso_unidade === 'ton' ? 'ton' : 
                        formData.peso_unidade === 'kg' ? 'kg' : 'm³';

    if (formData.tipo_valor === 'tonelada' && peso > 0 && valor > 0) {
      const total = valor * peso;
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
          <p className="text-sm text-blue-800">
            <Info className="w-4 h-4 inline mr-1" />
            <strong>Total:</strong> {formatarValor(total)} 
            <span className="text-blue-600"> ({formData.valor_frete} × {formData.peso_valor} {unidadeLabel} = {total})</span>
          </p>
        </div>
      );
    }
    if (formData.tipo_valor === 'metro_cubico' && m3 > 0 && valor > 0) {
      const total = valor * m3;
      return (
        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 mt-2">
          <p className="text-sm text-violet-800">
            <Info className="w-4 h-4 inline mr-1" />
            <strong>Total:</strong> {formatarValor(total)}
            <span className="text-violet-600"> ({formData.valor_frete} × {formData.metros_cubicos} m³ = {total})</span>
          </p>
        </div>
      );
    }
    return null;
  };

  const renderEmpresaPreview = () => {
    if (!user) return null;

    if (verificandoEmpresa) {
      return (
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
              <span className="text-sm text-amber-800">Verificando aprovação da empresa...</span>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!empresaAprovada) {
      return (
        <Card className="bg-red-50 border-red-200 mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-red-900">
              <Shield className="w-5 h-5 text-red-600" />
              Empresa Não Aprovada
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-red-100">
              <Info className="w-4 h-4 text-red-500" />
              <span className="text-slate-700">
                Sua empresa ainda não foi aprovada pelo administrador. 
                Você não pode publicar cargas até a aprovação ser concluída.
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Status atual: <Badge variant="outline" className="text-amber-600 border-amber-300">Em Análise</Badge>
            </p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="bg-green-50 border-green-200 mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-green-900">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Empresa Aprovada
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center gap-2 p-3 bg-white rounded-lg border border-green-100">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span className="text-slate-700">
              ✅ Sua empresa está aprovada! Você pode publicar cargas normalmente.
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Empresa: <strong>{formData.empresa_nome}</strong> | CNPJ: {formData.empresa_cnpj || 'N/A'}
          </p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate('/minhas-cargas')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <h1 className="text-3xl font-bold text-[#1E3A5F]">Nova Carga</h1>
          <p className="text-slate-600 mt-1">Cadastre uma nova carga para transporte</p>
        </div>

        {renderEmpresaPreview()}

        {!empresaAprovada && !verificandoEmpresa && (
          <Card className="bg-gray-100 border-gray-200 mb-6">
            <CardContent className="p-6 text-center">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Publicação Bloqueada</h3>
              <p className="text-sm text-gray-500 mb-4">
                Aguarde a aprovação da sua empresa pelo administrador para publicar cargas.
              </p>
              <Button onClick={() => navigate('/perfil')} variant="outline">
                Ver Meu Perfil
              </Button>
            </CardContent>
          </Card>
        )}

        {(empresaAprovada || verificandoEmpresa) && (
          <Tabs defaultValue="individual" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="individual" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Cadastro Individual
              </TabsTrigger>
              <TabsTrigger value="massa" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Importação em Massa
              </TabsTrigger>
            </TabsList>

            <TabsContent value="individual">
              <form onSubmit={handleSubmit} className="space-y-6">

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Package className="w-5 h-5 text-[#FF6B35]" />
                      Informações da Carga
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label>Título da carga *</Label>
                      <Input
                        placeholder="Ex: Carga de grãos para São Paulo"
                        value={formData.titulo}
                        onChange={(e) => handleChange('titulo', e.target.value)}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de Carga *</Label>
                        <Select 
                          value={formData.tipo_carga} 
                          onValueChange={(v) => handleChange('tipo_carga', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {tiposCarga.map(tipo => (
                              <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <Weight className="w-4 h-4" />
                        Quantidade / Peso / Volume *
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="Ex: 45"
                          value={formData.peso_valor}
                          onChange={(e) => handleChange('peso_valor', e.target.value)}
                          required
                        />
                        <Select 
                          value={formData.peso_unidade} 
                          onValueChange={(v) => handleChange('peso_unidade', v)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Unidade" />
                          </SelectTrigger>
                          <SelectContent>
                            {unidadesPeso.map(u => (
                              <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-500">
                        Escolha a unidade adequada. O valor será exibido nos cards exatamente como informado.
                      </p>
                    </div>

                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <List className="w-4 h-4" />
                        Descrição da Carga * (Selecione as frases)
                      </Label>

                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <p className="text-sm text-slate-600 mb-3">
                          Clique nas frases para selecionar/deselecionar:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {frasesDescricao.map((frase) => {
                            const selecionada = formData.frases_selecionadas.includes(frase.value);
                            return (
                              <button
                                key={frase.value}
                                type="button"
                                onClick={() => toggleFrase(frase.value)}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                                  selecionada
                                    ? 'bg-[#FF6B35] text-white border-[#FF6B35] shadow-md'
                                    : 'bg-white text-slate-700 border-slate-300 hover:border-[#FF6B35] hover:text-[#FF6B35]'
                                }`}
                              >
                                {selecionada && <CheckCircle className="w-3 h-3 inline mr-1" />}
                                {frase.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {formData.frases_selecionadas.length > 0 && (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <p className="text-xs text-emerald-700 font-medium mb-1">Descrição final:</p>
                          <p className="text-sm text-emerald-900">{montarDescricao()}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <MapPin className="w-5 h-5 text-[#FF6B35]" />
                      Origem e Destino *
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Cidade de Origem *</Label>
                        <Input
                          placeholder="Ex: Cuiabá"
                          value={formData.origem_cidade}
                          onChange={(e) => handleChange('origem_cidade', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Estado de Origem *</Label>
                        <Select 
                          value={formData.origem_estado} 
                          onValueChange={(v) => handleChange('origem_estado', v)}
                          required
                        >
                          <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            {estadosBrasil.map(uf => (
                              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Cidade de Destino *</Label>
                        <Input
                          placeholder="Ex: Santos"
                          value={formData.destino_cidade}
                          onChange={(e) => handleChange('destino_cidade', e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Estado de Destino *</Label>
                        <Select 
                          value={formData.destino_estado} 
                          onValueChange={(v) => handleChange('destino_estado', v)}
                          required
                        >
                          <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                          <SelectContent>
                            {estadosBrasil.map(uf => (
                              <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Truck className="w-5 h-5 text-[#FF6B35]" />
                      Veículo Necessário *
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Tipo de Veículo *</Label>
                        <Select 
                          value={formData.tipo_veiculo} 
                          onValueChange={(v) => handleChange('tipo_veiculo', v)}
                          required
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {tiposVeiculo.map(tipo => (
                              <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de Carroceria</Label>
                        <Select 
                          value={formData.tipo_carroceria} 
                          onValueChange={(v) => handleChange('tipo_carroceria', v)}
                        >
                          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                          <SelectContent>
                            {tiposCarroceria.map(tipo => (
                              <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Calendar className="w-5 h-5 text-[#FF6B35]" />
                      Datas e Valor *
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data de Coleta</Label>
                        <Input
                          type="date"
                          min={new Date().toISOString().split('T')[0]}
                          value={formData.data_coleta}
                          onChange={(e) => handleChange('data_coleta', e.target.value)}
                        />
                        <p className="text-xs text-green-600">✅ Preenchido automaticamente (hoje)</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Data Limite de Entrega</Label>
                        <Input
                          type="date"
                          min={new Date().toISOString().split('T')[0]}
                          value={formData.data_entrega}
                          onChange={(e) => handleChange('data_entrega', e.target.value)}
                        />
                        <p className="text-xs text-blue-600">✅ Preenchido automaticamente (+5 dias)</p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-4 border-t">
                      <Label className="flex items-center gap-2 text-base">
                        <DollarSign className="w-5 h-5" />
                        Tipo de Valor *
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        {tiposValor.map((tipo) => (
                          <button
                            key={tipo.value}
                            type="button"
                            onClick={() => handleChange('tipo_valor', tipo.value)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              formData.tipo_valor === tipo.value 
                                ? 'border-[#FF6B35] bg-orange-50 ring-2 ring-[#FF6B35]/20' 
                                : 'border-slate-200 hover:border-orange-300'
                            }`}
                          >
                            <div className="font-semibold text-slate-900 text-sm">{tipo.label}</div>
                            <div className="text-xs text-slate-500 mt-1">{tipo.descricao}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {formData.tipo_valor !== 'a_combinar' && (
                      <div className="space-y-2 pt-4">
                        <Label>
                          {formData.tipo_valor === 'fixo' && 'Valor do Frete (R$) *'}
                          {formData.tipo_valor === 'tonelada' && 'Valor por unidade (R$) *'}
                          {formData.tipo_valor === 'metro_cubico' && 'Valor por m³ (R$/m³) *'}
                        </Label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={formData.valor_frete}
                          onChange={(e) => handleChange('valor_frete', e.target.value)}
                          required
                        />
                        {renderValorPreview()}
                      </div>
                    )}

                    {formData.tipo_valor === 'a_combinar' && (
                      <div className="bg-slate-100 rounded-lg p-4 mt-4">
                        <p className="text-sm text-slate-700 flex items-center gap-2">
                          <Info className="w-4 h-4 text-blue-500" />
                          Valor será negociado diretamente com o motorista.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/minhas-cargas')}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSaving || !empresaAprovada}
                    className={`flex-1 ${empresaAprovada ? 'bg-[#FF6B35] hover:bg-[#E85A2A]' : 'bg-gray-400 cursor-not-allowed'}`}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Publicando...
                      </>
                    ) : !empresaAprovada ? (
                      <>
                        <Shield className="w-4 h-4 mr-2" />
                        Aguardando Aprovação
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Publicar Carga
                      </>
                    )}
                  </Button>
                </div>
              </form>

              {/* ✅ NOVO: MODAL DE LINK DE DIVULGAÇÃO */}
              {linkDivulgacao && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <Card className="w-full max-w-lg bg-white shadow-2xl border-2 border-[#FF6B35]">
                    <CardHeader className="bg-gradient-to-r from-[#FF6B35] to-[#E85A2A] text-white rounded-t-lg">
                      <CardTitle className="flex items-center gap-2 text-xl">
                        <Share2 className="w-6 h-6" />
                        Carga Publicada com Sucesso!
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6 space-y-4">
                      <div className="text-center">
                        <img src="/borinhochat.png" alt="Borinho" className="w-16 h-16 mx-auto mb-3 animate-bounce" />
                        <p className="text-slate-700 font-medium">
                          Sua carga está no ar! Compartilhe o link com os motoristas.
                        </p>
                      </div>

                      {/* LINK */}
                      <div className="bg-slate-100 border-2 border-slate-200 rounded-lg p-3">
                        <p className="text-xs text-slate-500 mb-1 uppercase tracking-wide">Link de Divulgação</p>
                        <p className="text-sm font-mono text-slate-800 break-all">{linkDivulgacao}</p>
                      </div>

                      {/* BOTÕES */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Button 
                          onClick={copiarLink}
                          className={`w-full ${linkCopiado ? 'bg-green-600 hover:bg-green-700' : 'bg-[#FF6B35] hover:bg-[#E85A2A]'}`}
                        >
                          {linkCopiado ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                          {linkCopiado ? 'Copiado!' : 'Copiar Link'}
                        </Button>
                        
                        <Button 
                          onClick={compartilharWhatsApp}
                          variant="outline"
                          className="w-full border-green-500 text-green-700 hover:bg-green-50"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          WhatsApp
                        </Button>
                      </div>

                      <Button 
                        onClick={fecharEIrParaCargas}
                        variant="ghost" 
                        className="w-full text-slate-500 hover:text-slate-700"
                      >
                        Ir para Minhas Cargas
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            <TabsContent value="massa">
              <div className="space-y-6">
                <Card className="border-l-4 border-l-[#FF6B35]">
                  <CardHeader>
                    <CardTitle className="text-lg">Importação em Massa</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-800 font-medium mb-2">
                        <Info className="w-4 h-4 inline mr-1" />
                        Colunas obrigatórias no CSV:
                      </p>
                      <ul className="text-xs text-amber-700 space-y-1 ml-4">
                        <li>• <strong>peso_valor</strong> - número (ex: 45)</li>
                        <li>• <strong>peso_unidade</strong> - kg, ton, ou m3</li>
                        <li>• <strong>tipo_valor</strong> - fixo, a_combinar, tonelada, metro_cubico</li>
                        <li>• <strong>valor_frete</strong> - número (ex: 150.00 ou 150,00)</li>
                        <li>• <strong>descricao</strong> - texto completo</li>
                      </ul>
                    </div>

                    <Button onClick={downloadCSVTemplate} variant="outline" className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Baixar Modelo CSV
                    </Button>

                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label htmlFor="csv-upload" className="cursor-pointer block">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">
                          {csvFile ? csvFile.name : 'Clique para selecionar arquivo CSV'}
                        </p>
                      </label>
                    </div>

                    {csvFile && (
                      <Button 
                        onClick={importCSV}
                        disabled={isImporting || !empresaAprovada}
                        className={`w-full ${empresaAprovada ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                      >
                        {isImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        {!empresaAprovada ? 'Aguardando Aprovação' : 'Importar Cargas'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}